import { Component } from '@theme/component';

class YumiEditorialCarousel extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.track = this.querySelector('[data-yec-track]');
    this.panel = this.querySelector('[data-yec-panel]');
    this.dots = this.querySelector('[data-yec-dots]');
    if (!this.track || !this.panel) return;
    this.slides = [...this.track.children].filter(slide => !slide.hasAttribute('data-yec-clone'));
    this.copies = this.slides.map(slide => slide.querySelector('[data-yec-copy]'));
    this.activeIndex = 0;
    this.mobile = matchMedia('(max-width: 749px)');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.motion.addEventListener('change', () => this.updateVideos(), { signal });
    this.dots.replaceChildren(...this.slides.map((slide, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute('aria-controls', this.track.id);
      dot.setAttribute('aria-label', this.dots.dataset.label.replace('[number]', String(index + 1)));
      dot.addEventListener('click', () => this.go(index), { signal });
      return dot;
    }));
    this.dots.hidden = this.slides.length < 2;
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => this.update());
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.normalize(), 160);
    }, { passive: true, signal });
    this.track.addEventListener('scrollend', () => this.normalize(), { signal });
    this.track.addEventListener('keydown', event => {
      if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home') this.go(0);
      else if (event.key === 'End') this.go(this.slides.length - 1);
      else this.move(event.key === 'ArrowRight' ? 1 : -1);
    }, { signal });
    this.track.addEventListener('dragstart', event => event.preventDefault(), { signal });
    this.track.addEventListener('pointerdown', event => this.pointerDown(event), { signal });
    this.track.addEventListener('pointermove', event => this.pointerMove(event), { signal });
    this.track.addEventListener('pointerup', event => this.pointerUp(event), { signal });
    this.track.addEventListener('pointercancel', event => this.pointerUp(event), { signal });
    this.track.addEventListener('click', event => {
      if (!this.suppressClick) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.suppressClick = false;
    }, { capture: true, signal });
    document.addEventListener('shopify:block:select', event => {
      const target = event.target;
      const index = this.slides.findIndex((slide, i) => slide === target || slide.contains(target) || this.copies[i]?.contains(target));
      if (index !== -1) this.go(index, true);
    }, { signal });
    document.addEventListener('visibilitychange', () => this.updateVideos(), { signal });
    this.visibilityObserver = new IntersectionObserver(entries => {
      this.visible = entries[0].isIntersecting;
      this.updateVideos();
    }, { threshold: 0.01 });
    this.visibilityObserver.observe(this.querySelector('.yec-viewport'));
    this.resizeObserver = new ResizeObserver(() => {
      if (Math.abs(this.track.clientWidth - this.step) > 1 || this.isMobile !== this.mobile.matches) this.build();
    });
    this.build();
    this.resizeObserver.observe(this.track);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    clearTimeout(this.settleTimer);
    cancelAnimationFrame(this.scrollFrame);
    cancelAnimationFrame(this.snapFrame);
    this.querySelectorAll('video').forEach(video => video.pause());
    this.track?.querySelectorAll('[data-yec-clone]').forEach(clone => clone.remove());
    this.copies?.forEach((copy, index) => {
      if (copy) { copy.hidden = false; this.slides[index].append(copy); }
    });
    this.drag = null;
    this.track?.style.removeProperty('scroll-snap-type');
    delete this.dataset.dragging;
  }

  build() {
    const index = this.activeIndex || 0;
    this.drag = null;
    delete this.dataset.dragging;
    this.track.querySelectorAll('[data-yec-clone]').forEach(clone => clone.remove());
    this.isMobile = this.mobile.matches;
    this.copies.forEach((copy, i) => {
      if (!copy) return;
      copy.hidden = !this.isMobile && i !== index;
      (this.isMobile ? this.slides[i] : this.panel).append(copy);
    });
    if (this.slides.length > 1) {
      this.track.prepend(...this.slides.map(slide => this.clone(slide)));
      this.track.append(...this.slides.map(slide => this.clone(slide)));
    }
    this.step = this.track.clientWidth;
    this.cycle = this.step * this.slides.length;
    this.origin = this.slides.length > 1 ? this.cycle : 0;
    this.instant(this.origin + index * this.step);
    this.dataset.ready = 'true';
    this.update();
  }

  clone(slide) {
    const clone = slide.cloneNode(true);
    clone.dataset.yecClone = 'true';
    clone.setAttribute('aria-hidden', 'true');
    for (const node of [clone, ...clone.querySelectorAll('*')]) {
      node.removeAttribute('id');
      node.removeAttribute('data-shopify-editor-block');
      node.removeAttribute('data-shopify-editor-section');
      if (node.matches('a,button,input,select,textarea,video,[tabindex]')) node.setAttribute('tabindex', '-1');
      if (node instanceof HTMLVideoElement) node.autoplay = false;
    }
    return clone;
  }

  instant(position) {
    cancelAnimationFrame(this.snapFrame);
    this.track.style.scrollSnapType = 'none';
    this.track.scrollTo({ left: position, behavior: 'instant' });
    this.snapFrame = requestAnimationFrame(() => {
      if (!this.drag) this.track.style.removeProperty('scroll-snap-type');
    });
  }

  update() {
    if (!this.step || !this.slides.length) return;
    const physical = Math.round(this.track.scrollLeft / this.step);
    this.activeIndex = ((physical % this.slides.length) + this.slides.length) % this.slides.length;
    this.dataset.activeIndex = String(this.activeIndex);
    [...this.dots.children].forEach((dot, index) => {
      if (index === this.activeIndex) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    if (!this.isMobile) this.copies.forEach((copy, i) => { if (copy) copy.hidden = i !== this.activeIndex; });
    this.updateVideos();
  }

  updateVideos() {
    if (!this.track || !this.step) return;
    const active = this.track.children[Math.round(this.track.scrollLeft / this.step)];
    this.track.querySelectorAll('video').forEach(video => {
      const play = active?.contains(video) && this.visible && !document.hidden && !this.motion.matches;
      if (play && video.paused) video.play().catch(() => {});
      else if (!play && !video.paused) video.pause();
    });
  }

  normalize() {
    if (this.drag || this.slides.length < 2 || !this.cycle) return;
    const offset = this.track.scrollLeft - this.origin;
    if (offset < -1 || offset >= this.cycle - 1) this.instant(this.origin + ((offset % this.cycle) + this.cycle) % this.cycle);
    this.update();
  }

  move(direction) {
    if (this.slides.length < 2) return;
    this.normalize();
    const raw = Math.round((this.track.scrollLeft - this.origin) / this.step);
    this.track.scrollTo({ left: this.origin + (raw + direction) * this.step, behavior: this.motion.matches ? 'instant' : 'smooth' });
  }

  go(index, immediately = false) {
    if (!this.step) return;
    this.normalize();
    let offset = index - this.activeIndex;
    if (Math.abs(offset) > this.slides.length / 2) offset -= Math.sign(offset) * this.slides.length;
    const target = this.origin + (this.activeIndex + offset) * this.step;
    if (immediately) { this.instant(target); this.update(); }
    else this.track.scrollTo({ left: target, behavior: this.motion.matches ? 'instant' : 'smooth' });
  }

  pointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button,a,input')) return;
    this.normalize();
    this.suppressClick = false;
    this.drag = { id: event.pointerId, x: event.clientX, position: this.track.scrollLeft, moved: false };
  }

  pointerMove(event) {
    if (!this.drag || this.drag.id !== event.pointerId) return;
    const delta = event.clientX - this.drag.x;
    if (!this.drag.moved && Math.abs(delta) < 6) return;
    this.drag.moved = true;
    this.suppressClick = true;
    this.dataset.dragging = 'true';
    this.track.setPointerCapture(event.pointerId);
    this.track.style.scrollSnapType = 'none';
    event.preventDefault();
    this.track.scrollLeft = this.drag.position - delta;
    const offset = this.track.scrollLeft - this.origin;
    if (this.cycle && (offset < -this.cycle / 2 || offset > this.cycle * 1.5)) {
      const shift = offset < 0 ? this.cycle : -this.cycle;
      this.track.scrollLeft += shift;
      this.drag.position += shift;
    }
  }

  pointerUp(event) {
    if (!this.drag || this.drag.id !== event.pointerId) return;
    const { moved, x, position } = this.drag;
    const delta = x - event.clientX;
    let target = Math.round(this.track.scrollLeft / this.step);
    if (moved && Math.abs(delta) > Math.min(60, this.step * 0.15)) {
      const start = Math.round(position / this.step);
      target = delta > 0 ? Math.max(start + 1, target) : Math.min(start - 1, target);
    }
    this.drag = null;
    if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
    delete this.dataset.dragging;
    this.track.style.removeProperty('scroll-snap-type');
    if (moved) this.track.scrollTo({ left: target * this.step, behavior: this.motion.matches ? 'instant' : 'smooth' });
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => this.normalize(), 180);
  }
}

if (!customElements.get('yumi-editorial-carousel')) customElements.define('yumi-editorial-carousel', YumiEditorialCarousel);
